import { useMemo } from 'react';

import { HistoryContent } from '../types';

type NextNData = {
  pages: {
    nextNPublishedScheduledContent: {
      edges: { node: { body: unknown } }[];
    };
  }[];
};

interface UseGapBoundedNextIndexParams<T extends HistoryContent> {
  revisions: T[];
  hasNewerFromWindow: boolean | undefined;
  liveVersion: T | undefined;
  windowItems: T[];
  nextNFromWindowData: NextNData | undefined;
}

// While the nextN walker hasn't closed the gap with the live-anchored chain, navigation must
// stop at the top of the nextN/window chain. Otherwise Next would skip the gap and land in the
// live chain. Once `hasNewerFromWindow` flips to false, the gap has closed and the full
// `revisions` range is contiguous again.
export const useGapBoundedNextIndex = <T extends HistoryContent>({
  revisions,
  hasNewerFromWindow,
  liveVersion,
  windowItems,
  nextNFromWindowData
}: UseGapBoundedNextIndexParams<T>): number =>
  useMemo(() => {
    if (!hasNewerFromWindow) return revisions.length - 1;

    const live = liveVersion?.metadata.publishUtc;
    const candidates: number[] = [];
    const consider = (u: number | null | undefined) => {
      if (u == null) return;
      if (live != null && u >= live) return;
      candidates.push(u);
    };
    windowItems.forEach((i) => consider(i.metadata.publishUtc));
    nextNFromWindowData?.pages.forEach((p) =>
      p.nextNPublishedScheduledContent.edges.forEach((e) => consider((e.node.body as T).metadata.publishUtc))
    );

    if (candidates.length === 0) return revisions.length - 1;
    const max = Math.max(...candidates);
    const idx = revisions.findIndex((c) => c.metadata.publishUtc === max);
    return idx !== -1 ? idx : revisions.length - 1;
  }, [revisions, hasNewerFromWindow, liveVersion, windowItems, nextNFromWindowData]);
